/**
 * Alineación magnética al mover y al redimensionar: lo que hace que un diagrama
 * armado a mano se vea prolijo sin trabajo.
 *
 * Puro, sin Pixi ni DOM, como `edgeGeometry`: entran el rectángulo que se está
 * manipulando y los que están quietos, sale cuánto corregir y qué guías dibujar.
 *
 * Se comparan tres líneas por eje —los dos bordes y el centro— pero NO las nueve
 * combinaciones posibles. Enganchar el borde izquierdo de algo contra el CENTRO
 * de otra cosa es una coincidencia sin significado: no deja nada alineado, y era
 * la fuente principal de imanes que agarran donde no se quiere. Quedan cinco
 * combinaciones con sentido (ver `COMBOS`).
 *
 * Encima de eso va el reparto equidistante (`snapSpacing`): no alinea bordes
 * sino HUECOS, que es la otra mitad de lo que hace que una fila armada a mano
 * se vea hecha a máquina. Los dos compiten por el mismo eje y gana el que
 * corrija menos.
 */

export interface SnapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapResult {
  /** Corrección a sumar al movimiento, en coordenadas de mundo. */
  dx: number;
  dy: number;
  /** Coordenada de mundo de la guía vertical que se enganchó, si hay. */
  guideX: number | null;
  /** Ídem horizontal. */
  guideY: number | null;
  /**
   * Extremos (min, max) del tramo que cubre cada guía sobre el eje PERPENDICULAR
   * al suyo: la vertical se dibuja entre estos dos valores de Y. Así la guía
   * señala QUÉ se alineó con qué, en vez de cruzar la pantalla entera y dejar al
   * ojo adivinar cuál de las diez cajas de la fila fue la que enganchó.
   */
  spanY: [number, number] | null;
  spanX: [number, number] | null;
  /**
   * Huecos iguales que quedaron repartidos, para dibujarlos como medidas.
   * `gapsX` son huecos HORIZONTALES (reparto sobre X) y `gapsY` verticales.
   * Vacíos cuando en ese eje ganó la alineación de bordes, que es otra cosa.
   */
  gapsX: SnapGap[];
  gapsY: SnapGap[];
}

/** Un hueco medido: de dónde a dónde, y a qué altura se dibuja la barrita. */
export interface SnapGap {
  /** Extremos del hueco sobre el eje del reparto. */
  from: number;
  to: number;
  /** Coordenada sobre el eje PERPENDICULAR donde se centra la barrita. */
  cross: number;
}

const START = 0;
const CENTER = 1;
const END = 2;

/**
 * Pares (línea del que se mueve, línea del candidato) que se prueban, EN ORDEN
 * DE PREFERENCIA: ante dos enganches igual de cercanos gana el primero de esta
 * lista. Bordes homólogos primero (la columna de cajas con el izquierdo
 * compartido, el caso más común de un diagrama), después centros, y por último
 * borde contra borde opuesto, que es "pegado a", no "alineado con".
 */
const COMBOS: ReadonlyArray<readonly [number, number]> = [
  [START, START],
  [END, END],
  [CENTER, CENTER],
  [END, START],
  [START, END],
];

/** Las tres líneas de un rect sobre el eje X: izquierda, centro, derecha. */
function linesX(r: SnapRect): [number, number, number] {
  return [r.x, r.x + r.width / 2, r.x + r.width];
}

function linesY(r: SnapRect): [number, number, number] {
  return [r.y, r.y + r.height / 2, r.y + r.height];
}

function spanX(r: SnapRect): [number, number] {
  return [r.x, r.x + r.width];
}

function spanY(r: SnapRect): [number, number] {
  return [r.y, r.y + r.height];
}

interface AxisSnap {
  delta: number;
  line: number | null;
  span: [number, number] | null;
}

const NO_SNAP: AxisSnap = { delta: 0, line: null, span: null };

/**
 * El tramo que cubre la guía: lo que se movió más TODOS los que comparten la
 * línea enganchada. Si tres cajas quedan con el mismo borde izquierdo, la guía
 * las abarca a las tres.
 */
function spanFor(
  line: number,
  movingSpan: [number, number],
  others: readonly SnapRect[],
  linesOf: (r: SnapRect) => [number, number, number],
  spanOf: (r: SnapRect) => [number, number],
  eps: number,
): [number, number] {
  let min = movingSpan[0];
  let max = movingSpan[1];
  for (const o of others) {
    const l = linesOf(o);
    if (Math.abs(l[0] - line) > eps && Math.abs(l[1] - line) > eps && Math.abs(l[2] - line) > eps) {
      continue;
    }
    const s = spanOf(o);
    if (s[0] < min) min = s[0];
    if (s[1] > max) max = s[1];
  }
  return [min, max];
}

/**
 * El mejor enganche de un eje. `movingLines` son las tres líneas de lo que se
 * mueve; se recorren los candidatos y se elige la corrección más chica,
 * desempatando por el orden de `COMBOS`.
 */
function snapAxis(
  movingLines: [number, number, number],
  movingSpan: [number, number],
  others: readonly SnapRect[],
  tolerance: number,
  linesOf: (r: SnapRect) => [number, number, number],
  spanOf: (r: SnapRect) => [number, number],
): AxisSnap {
  // Un desempate exacto por igualdad de punto flotante no existe: dos enganches
  // "iguales" difieren en la última cifra. El epsilon los junta para que decida
  // la preferencia y no el redondeo.
  const eps = tolerance * 1e-3;
  let bestDist = Infinity;
  let bestOrder = Infinity;
  let bestDelta = 0;
  let bestLine: number | null = null;

  for (const other of others) {
    const ol = linesOf(other);
    for (let k = 0; k < COMBOS.length; k++) {
      const [mi, oi] = COMBOS[k];
      const d = ol[oi] - movingLines[mi];
      const dist = Math.abs(d);
      if (dist > tolerance) continue;
      if (dist < bestDist - eps || (dist <= bestDist + eps && k < bestOrder)) {
        bestDist = dist;
        bestOrder = k;
        bestDelta = d;
        bestLine = ol[oi];
      }
    }
  }

  if (bestLine === null) return NO_SNAP;
  return {
    delta: bestDelta,
    line: bestLine,
    span: spanFor(bestLine, movingSpan, others, linesOf, spanOf, eps),
  };
}

/**
 * Adaptador de eje: deja escribir el reparto UNA vez y usarlo para los huecos
 * horizontales y los verticales. Sin esto son dos copias de cuarenta líneas que
 * se diferencian en `x`/`y`, y la segunda copia siempre se queda atrás.
 */
interface Axis {
  /** Borde de arranque sobre el eje del reparto (izquierdo o superior). */
  lo(r: SnapRect): number;
  /** Borde de llegada (derecho o inferior). */
  hi(r: SnapRect): number;
  size(r: SnapRect): number;
  /** Extremos sobre el eje PERPENDICULAR, los que deciden si están en la misma fila. */
  crossLo(r: SnapRect): number;
  crossHi(r: SnapRect): number;
}

const AXIS_X: Axis = {
  lo: (r) => r.x,
  hi: (r) => r.x + r.width,
  size: (r) => r.width,
  crossLo: (r) => r.y,
  crossHi: (r) => r.y + r.height,
};

const AXIS_Y: Axis = {
  lo: (r) => r.y,
  hi: (r) => r.y + r.height,
  size: (r) => r.height,
  crossLo: (r) => r.x,
  crossHi: (r) => r.x + r.width,
};

/** `null` en un par de huecos es lo que se está moviendo. */
type GapPair = readonly [SnapRect | null, SnapRect | null];

interface SpacingSnap {
  delta: number;
  pairs: GapPair[];
}

/** El vecino inmediato de `r` hacia atrás sobre el eje, o null. */
function prevOf(row: readonly SnapRect[], r: SnapRect, ax: Axis, eps: number): SnapRect | null {
  let best: SnapRect | null = null;
  for (const o of row) {
    if (o === r || ax.hi(o) > ax.lo(r) + eps) continue;
    if (!best || ax.hi(o) > ax.hi(best)) best = o;
  }
  return best;
}

/** Ídem hacia adelante. */
function nextOf(row: readonly SnapRect[], r: SnapRect, ax: Axis, eps: number): SnapRect | null {
  let best: SnapRect | null = null;
  for (const o of row) {
    if (o === r || ax.lo(o) < ax.hi(r) - eps) continue;
    if (!best || ax.lo(o) < ax.lo(best)) best = o;
  }
  return best;
}

/**
 * El reparto equidistante sobre un eje: cuánto correr lo que se mueve para que
 * los huecos que deja queden iguales.
 *
 * Se miran sólo los que comparten banda en el eje perpendicular. Repartir el
 * espacio contra algo que está en otra fila no significa nada —el hueco que se
 * mediría no existe para el ojo— y sin ese filtro cualquier cosa lejana entra a
 * proponer separaciones.
 *
 * Tres jugadas, que son las que de verdad se hacen:
 *  - **Centrar**: cae entre dos vecinos y queda a la misma distancia de los dos.
 *  - **Seguir la fila**: hay dos cajas antes con una separación; se repite.
 *  - **Encabezar la fila**: ídem con las dos cajas de después.
 */
function snapSpacing(
  moving: SnapRect,
  others: readonly SnapRect[],
  tolerance: number,
  ax: Axis,
): SpacingSnap | null {
  const eps = tolerance * 1e-3;
  const row = others.filter(
    (o) => ax.crossLo(o) < ax.crossHi(moving) && ax.crossHi(o) > ax.crossLo(moving),
  );
  if (row.length < 2) return null;

  // Vecinos inmediatos de lo que se mueve, uno a cada lado.
  let before: SnapRect | null = null;
  let after: SnapRect | null = null;
  for (const o of row) {
    if (ax.hi(o) <= ax.lo(moving) + tolerance && (!before || ax.hi(o) > ax.hi(before))) before = o;
    if (ax.lo(o) >= ax.hi(moving) - tolerance && (!after || ax.lo(o) < ax.lo(after))) after = o;
  }

  const cands: SpacingSnap[] = [];
  // `target` es dónde debería quedar el borde de arranque de lo que se mueve.
  const push = (target: number, pairs: GapPair[]): void => {
    const delta = target - ax.lo(moving);
    if (Math.abs(delta) <= tolerance) cands.push({ delta, pairs });
  };

  if (before && after) {
    const free = ax.lo(after) - ax.hi(before) - ax.size(moving);
    // Un hueco de cero ya lo resuelve la adyacencia de `snapMove`: acá sólo
    // interesa el reparto de un espacio que se ve.
    if (free > eps) {
      push(ax.hi(before) + free / 2, [
        [before, null],
        [null, after],
      ]);
    }
  }

  if (before) {
    const prev = prevOf(row, before, ax, eps);
    if (prev) {
      const gap = ax.lo(before) - ax.hi(prev);
      if (gap > eps) {
        push(ax.hi(before) + gap, [
          [prev, before],
          [before, null],
        ]);
      }
    }
  }

  if (after) {
    const next = nextOf(row, after, ax, eps);
    if (next) {
      const gap = ax.lo(next) - ax.hi(after);
      if (gap > eps) {
        push(ax.lo(after) - gap - ax.size(moving), [
          [null, after],
          [after, next],
        ]);
      }
    }
  }

  let best: SpacingSnap | null = null;
  for (const c of cands) {
    if (!best || Math.abs(c.delta) < Math.abs(best.delta)) best = c;
  }
  return best;
}

/** Los huecos ya medidos sobre la posición FINAL de lo que se movió. */
function buildGaps(pairs: readonly GapPair[], moved: SnapRect, ax: Axis): SnapGap[] {
  return pairs.map(([p, q]) => {
    const a = p ?? moved;
    const b = q ?? moved;
    return {
      from: ax.hi(a),
      to: ax.lo(b),
      // La barrita se centra en la banda que las dos cajas comparten: puesta en
      // el medio de una sola quedaría flotando al lado de la otra.
      cross: (Math.max(ax.crossLo(a), ax.crossLo(b)) + Math.min(ax.crossHi(a), ax.crossHi(b))) / 2,
    };
  });
}

/**
 * La corrección que alinea `moving` con alguno de `others`.
 *
 * `tolerance` va en unidades de MUNDO: quien llama la calcula dividiendo los
 * píxeles de pantalla por el zoom, para que el imán se sienta igual de fuerte
 * en cualquier acercamiento.
 */
export function snapMove(
  moving: SnapRect,
  others: readonly SnapRect[],
  tolerance: number,
): SnapResult {
  const eps = tolerance * 1e-3;
  const alignX = snapAxis(linesX(moving), spanY(moving), others, tolerance, linesX, spanY);
  const alignY = snapAxis(linesY(moving), spanX(moving), others, tolerance, linesY, spanX);
  const spaceX = snapSpacing(moving, others, tolerance, AXIS_X);
  const spaceY = snapSpacing(moving, others, tolerance, AXIS_Y);

  // Alinear un borde y repartir un hueco tiran del MISMO eje, así que en cada
  // uno manda uno solo: el que corrija menos, y ante empate la alineación, que
  // es la relación más básica de las dos. Los ejes se deciden por separado, y
  // por eso sale gratis el caso bueno de una fila —mismo borde superior por Y,
  // huecos iguales por X— con las dos guías a la vez.
  const wins = (space: SpacingSnap | null, align: AxisSnap): SpacingSnap | null =>
    space !== null && (align.line === null || Math.abs(space.delta) < Math.abs(align.delta) - eps)
      ? space
      : null;
  const onX = wins(spaceX, alignX);
  const onY = wins(spaceY, alignY);

  const dx = onX ? onX.delta : alignX.delta;
  const dy = onY ? onY.delta : alignY.delta;
  const moved: SnapRect = { ...moving, x: moving.x + dx, y: moving.y + dy };

  return {
    dx,
    dy,
    guideX: onX ? null : alignX.line,
    guideY: onY ? null : alignY.line,
    spanY: onX ? null : alignX.span,
    spanX: onY ? null : alignY.span,
    gapsX: onX ? buildGaps(onX.pairs, moved, AXIS_X) : [],
    gapsY: onY ? buildGaps(onY.pairs, moved, AXIS_Y) : [],
  };
}

/**
 * Ídem para un resize: acá NO se mueve la caja entera sino los bordes que el
 * puntero arrastra, así que sólo ésos son fuente de enganche. Alinear el centro
 * de algo que está cambiando de tamaño no significa nada — el centro se está
 * corriendo solo — y engancharlo dejaba el borde arrastrado en cualquier lado.
 *
 * `dir` es la dirección del gesto ("nw", "e", …); la corrección devuelta se le
 * suma al PUNTERO, que es lo que define el borde arrastrado.
 */
export function snapResize(
  moving: SnapRect,
  dir: string,
  others: readonly SnapRect[],
  tolerance: number,
): SnapResult {
  const west = dir.includes("w");
  const east = dir.includes("e");
  const north = dir.includes("n");
  const south = dir.includes("s");

  // Una sola línea viva por eje: la que el puntero arrastra. Se repite en las
  // tres posiciones para reusar `snapAxis` sin que las otras dos enganchen.
  const edgeX = west ? moving.x : moving.x + moving.width;
  const edgeY = north ? moving.y : moving.y + moving.height;

  const sx =
    west || east
      ? snapAxis([edgeX, edgeX, edgeX], spanY(moving), others, tolerance, linesX, spanY)
      : NO_SNAP;
  const sy =
    north || south
      ? snapAxis([edgeY, edgeY, edgeY], spanX(moving), others, tolerance, linesY, spanX)
      : NO_SNAP;

  // Redimensionar no reparte huecos: lo que se mueve es un borde, no la caja,
  // y "quedar a la misma distancia de dos vecinos" no describe ese gesto.
  return {
    dx: sx.delta,
    dy: sy.delta,
    guideX: sx.line,
    guideY: sy.line,
    spanY: sx.span,
    spanX: sy.span,
    gapsX: [],
    gapsY: [],
  };
}

/** Caja que envuelve a varios rectángulos, o null si no hay ninguno. */
export function boundsOf(rects: readonly SnapRect[]): SnapRect | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.width > maxX) maxX = r.x + r.width;
    if (r.y + r.height > maxY) maxY = r.y + r.height;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
